import { useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { getPath, isArrayIndex } from "../utils/path.js";
import { getShapeFieldConfig } from "../utils/refs.js";
import { resolveRef } from "../utils/schema-context.js";
import { getFieldConstraints } from "../validation/constraints.js";
import { useFormStore, useFormStoreSelector, useForms } from "./use-form-store.js";
import { useSchemaContext } from "./use-schema-context.js";
/**
 * Look up config for a path, with fallback to pattern matching for array items.
 * For example, `api.inputs.0.args` will also try `api.inputs.*.args`.
 */
function resolveFieldConfig(config, path) {
    if (!config)
        return undefined;
    // Try exact match first
    if (config[path])
        return config[path];
    // Try pattern matching - replace numeric indices with wildcards
    const segments = path.split('.');
    const hasArrayIndex = segments.some((s) => isArrayIndex(s));
    if (hasArrayIndex) {
        // Replace array indices with wildcards and try again
        const patternPath = segments.map((s) => (isArrayIndex(s) ? '*' : s)).join('.');
        if (config[patternPath])
            return config[patternPath];
    }
    return undefined;
}
export function useField({ path, schema, parentSchema, fieldName, parentRef, config: configProp }) {
    const store = useFormStore();
    const ctx = useSchemaContext();
    const forms = useForms();
    const { value, defaultValue, rawError, isTouched, isDirty, showAllErrors, formConfig } = useFormStoreSelector(useShallow((s) => ({
        value: getPath(s.values, path),
        defaultValue: getPath(s.defaultValues, path),
        rawError: s.errors[path],
        isTouched: s.touched.has(path),
        isDirty: s.dirty.has(path),
        showAllErrors: s.showAllErrors,
        formConfig: s.config
    })));
    // Only show error if field is touched OR showAllErrors is true (after validate/submit)
    const error = isTouched || showAllErrors ? rawError : undefined;
    // Resolve any $ref in the schema
    const resolvedSchema = useMemo(() => resolveRef(ctx, schema), [ctx, schema]);
    // Check if field is required based on parent schema
    const isRequired = useMemo(() => {
        if (!parentSchema || !fieldName)
            return false;
        const resolvedParent = resolveRef(ctx, parentSchema);
        return resolvedParent.required?.includes(fieldName) ?? false;
    }, [ctx, parentSchema, fieldName]);
    /**
     * Config resolution priority (first match wins):
     * 1. Path-based config from store (e.g., createFormStore({ config: { "user.name": { widget: "..." } } }))
     * 2. Config prop passed from parent component (e.g., <ShapeField config={{ title: "..." }} />)
     * 3. Shape-based config from forms registry (e.g., <FormProvider forms={{ myShape: { default: { ... } } }}>)
     */
    const config = useMemo(() => {
        // Try path-based config first (from form store)
        const pathConfig = resolveFieldConfig(formConfig, path);
        if (pathConfig)
            return pathConfig;
        // Use config passed from parent (already looked up by parent)
        if (configProp)
            return configProp;
        // Fall back to shape-based config from forms (using parent's $ref)
        return getShapeFieldConfig(forms, parentRef, fieldName);
    }, [formConfig, path, configProp, forms, parentRef, fieldName]);
    const constraints = useMemo(() => getFieldConstraints(resolvedSchema, parentSchema, fieldName), [resolvedSchema, parentSchema, fieldName]);
    const onChange = useCallback((newValue) => {
        // Convert empty strings to undefined for optional string fields
        // so they don't persist as "" in form values
        const normalized = newValue === '' && !isRequired && resolvedSchema?.type === 'string' ? undefined : newValue;
        store.getState().setFieldValue(path, normalized);
    }, [store, path, isRequired, resolvedSchema]);
    const onBlur = useCallback(() => {
        const s = store.getState();
        s.setFieldTouched(path, true);
        if (s.validateOnBlur)
            void s.validateField(path);
    }, [store, path]);
    const setError = useCallback((error) => store.getState().setFieldError(path, error), [store, path]);
    const clearError = useCallback(() => store.getState().clearFieldError(path), [store, path]);
    return {
        value: value,
        defaultValue: defaultValue,
        schema: resolvedSchema,
        isRequired,
        constraints,
        config,
        error,
        isTouched,
        isDirty,
        onChange,
        onBlur,
        setError,
        clearError
    };
}
